import {
  type AssetManifest,
  EncounterChestOpenedSchema,
  EncounterRejectedSchema,
  EncounterRescuedSchema,
  EncountersEventSchema,
  type EncounterView,
  type InventoryState,
  PeddlerBoughtSchema,
  withinEncounterReach,
} from '@shefira/shared';
import type Phaser from 'phaser';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { cue } from '../audio/cues.js';
import { EncounterChestLayer, onRoadChest } from '../game/encounter-chests.js';
import { encounterRefusalText, lotsText, rescuedText } from './encounter-text.js';
import { PeddlerPanel } from './peddler-panel.js';
import { uiFormat } from './text.js';

/** The little of a Colyseus room this needs. */
interface EncounterRoom {
  onMessage: (type: string, callback: (payload: unknown) => void) => unknown;
  send: (type: string, payload: unknown) => void;
}

export interface EncounterClientOptions {
  room: EncounterRoom;
  scene: Phaser.Scene;
  manifest: AssetManifest;
  icons: () => CssAtlas | null;
  /** A line of the chat from the game, apart from what players say. */
  notice: (text: string) => void;
  /** A refusal over the world, gone by itself. */
  refuse: (text: string) => void;
}

interface Tile {
  x: number;
  y: number;
}

/**
 * The client's side of the road encounters (ADR 0093): the list the server
 * sends whole, drawn as marks on the minimap and as chests on the road, the
 * peddler's window, and the lines that answer a purchase, an opened chest or
 * a rescue. It decides nothing: reach, purse and who already took are the
 * server's to judge.
 */
export class EncounterClient {
  private list: readonly EncounterView[] = [];
  private readonly chests: EncounterChestLayer;
  private readonly peddler: PeddlerPanel;

  constructor(private readonly options: EncounterClientOptions) {
    const { room } = options;
    this.chests = new EncounterChestLayer(options.scene, options.manifest);
    this.peddler = new PeddlerPanel(options.icons, (encounterId) => {
      room.send('peddler_buy', { encounterId });
    });
    room.onMessage('encounters', (payload: unknown) => {
      const parsed = EncountersEventSchema.safeParse(payload);
      if (parsed.success) this.apply(parsed.data.encounters);
    });
    room.onMessage('peddler_bought', (payload: unknown) => {
      const parsed = PeddlerBoughtSchema.safeParse(payload);
      if (!parsed.success) return;
      cue('sfx.coins');
      this.peddler.showBought(parsed.data);
    });
    room.onMessage('encounter_chest_opened', (payload: unknown) => {
      const parsed = EncounterChestOpenedSchema.safeParse(payload);
      if (!parsed.success) return;
      cue('sfx.discover');
      options.notice(
        uiFormat("Le coffre s'ouvre et laisse tomber : {items}.", {
          items: lotsText(parsed.data.items),
        }),
      );
    });
    room.onMessage('encounter_rescued', (payload: unknown) => {
      const parsed = EncounterRescuedSchema.safeParse(payload);
      if (parsed.success) options.notice(rescuedText(parsed.data));
    });
    room.onMessage('encounter_rejected', (payload: unknown) => {
      const parsed = EncounterRejectedSchema.safeParse(payload);
      if (!parsed.success) return;
      cue('sfx.error');
      // The peddler's window tells its own refusals; a chest's go over the world.
      const id = this.peddler.encounterId;
      if (id !== undefined && (parsed.data.encounterId ?? id) === id)
        this.peddler.showRejected(parsed.data);
      else options.refuse(encounterRefusalText(parsed.data));
    });
  }

  /** The encounters as last told, for the minimap. */
  get encounters(): readonly EncounterView[] {
    return this.list;
  }

  /** The lost chest drawn over a tile, if any. */
  chestAt(tile: Tile): EncounterView | undefined {
    return this.list.find(
      (encounter) => encounter.kind === 'lost-chest' && onRoadChest(encounter, tile),
    );
  }

  /** Whether the player stands near enough a chest to open it, as the server will judge. */
  reaches(encounter: EncounterView, at: Tile): boolean {
    return withinEncounterReach(at, encounter);
  }

  /** Ask the server to open a lost chest; what falls, or why not, comes back. */
  open(encounter: EncounterView): void {
    if (encounter.done) {
      this.options.refuse(encounterRefusalText({ reason: 'opened' }));
      return;
    }
    this.options.room.send('encounter_open', { encounterId: encounter.id });
  }

  /**
   * The peddler clicked stands on his encounter's tile: the nearest peddler
   * encounter is his. False when none is known (the list has not come yet).
   */
  openPeddler(npc: Tile): boolean {
    const encounter = this.peddlerNear(npc);
    if (encounter === undefined) return false;
    cue('sfx.open');
    this.peddler.open(encounter);
    return true;
  }

  get peddlerOpen(): boolean {
    return this.peddler.isOpen;
  }

  /** While the window is open: the peddler's site, to measure the reach against. */
  peddlerSite(): Tile | undefined {
    const id = this.peddler.encounterId;
    return id === undefined ? undefined : this.list.find((encounter) => encounter.id === id);
  }

  closePeddler(): void {
    this.peddler.close();
  }

  setInventory(state: InventoryState): void {
    this.peddler.setInventory(state);
  }

  destroy(): void {
    this.chests.destroy();
    this.peddler.destroy();
  }

  private peddlerNear(tile: Tile): EncounterView | undefined {
    let best: EncounterView | undefined;
    let bestDistance = Infinity;
    for (const encounter of this.list) {
      if (encounter.kind !== 'peddler') continue;
      const distance = Math.max(Math.abs(encounter.x - tile.x), Math.abs(encounter.y - tile.y));
      if (distance < bestDistance) {
        best = encounter;
        bestDistance = distance;
      }
    }
    return best;
  }

  private apply(next: readonly EncounterView[]): void {
    this.list = next;
    this.chests.show(next);
    const id = this.peddler.encounterId;
    if (id !== undefined) this.peddler.setEncounter(next.find((encounter) => encounter.id === id));
  }
}
